import { FormApi } from "final-form";
import { Form, useForm, useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useSelector } from "src/store";
import { isValidApkUrl } from "src/ui/utils/validityChecker";

import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { HostedApk, Provisioning } from "@dashboard/provisioning/types";
import { Row, SectionDescription } from "@dashboard/shared/styles";
import { Typography, useTheme } from "@mui/material";
import { DangerOutlinedButton, PrimaryOutlinedButton } from "@shared/CustomButton";
import { TextField } from "@shared/form/TextField";
import {
    MinimalTable,
    MinimalTableBody,
    MinimalTableCell,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
    MinimalTableRow,
} from "@shared/table/MinimalTableComponents";

import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
    useCollapsiblePersistence,
} from "../../shared/components/CollapsibleSection";
import { requiredInternetConfigMessage } from "../utils";

import { hasInternetConfig } from "./InternetConfig/InternetConfig.utils";

const initialValues = { name: undefined, url: undefined, comment: null };

type FormValues = HostedApk;

export const HostedApks = () => {
    const { value, onChange } = useCollapsiblePersistence("provisioningPage.collapsedSections", "HostedApks");
    const parentForm = useForm();
    const theme = useTheme();
    const { t } = useTranslation(["pages", "common"]);

    const provisionings = useSelector(selectProvisionings, shallowEqual);
    const { values: parentFormValues } = useFormState<Partial<Provisioning>>();
    const isDisabled = !hasInternetConfig(parentFormValues?.settings?.internetConfig);
    const apps = parentFormValues?.settings?.hostedApks || [];
    const summary = apps.length ? t("pages:provisioningPage.section.hostedApks.summary", { count: apps.length }) : undefined;

    const onSubmit = (values: FormValues, form: FormApi<FormValues>) => {
        const newValue = { ...values, comment: values.comment || null };
        if (apps.length) {
            parentForm.change("settings.hostedApks", [...apps, newValue]);
        }
        if (!apps.length) {
            parentForm.change("settings.hostedApks", [newValue]);
        }
        form.restart(initialValues);
    };
    const validate = (values: FormValues) => {
        const errors: Partial<FormValues> = {};
        if (!values.name) {
            errors.name = t("pages:provisioningPage.input.name.validation.empty");
        }
        if (!values.url) {
            errors.url = t("pages:provisioningPage.input.url.validation.empty");
        }
        // `values` is the live form object, never an element of the list, so the previous
        // `includes` compared references and could never match - the message below was unreachable.
        if (apps.some((app) => app.name === values.name)) {
            errors.name = t("pages:provisioningPage.input.name.validation.alreadyAdded");
        }
        if (!isValidApkUrl(values.url)) {
            errors.url = t("pages:provisioningPage.input.url.validation.invalid");
        }
        return errors;
    };
    const handleDelete = (index: number) => {
        parentForm.change(
            "settings.hostedApks",
            apps.filter((_, i) => i !== index),
        );
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({ form }) => (
                <CollapsibleProvider initialValue={value} onChange={onChange} disabled={isDisabled}>
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle summary={summary}>
                            {t("pages:provisioningPage.section.hostedApks.title")}
                        </CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails error={isDisabled ? requiredInternetConfigMessage : undefined}>
                        <SectionDescription>{t("pages:provisioningPage.section.hostedApks.description")}</SectionDescription>
                        <MinimalTableContainer>
                            <MinimalTable>
                                <MinimalTableHeader>
                                    <MinimalTableRow>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">
                                                {t("pages:provisioningPage.table.header.name")}
                                            </Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">{t("common:label.url")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">{t("common:label.comment")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="right"></MinimalTableHeaderCell>
                                    </MinimalTableRow>
                                </MinimalTableHeader>
                                <MinimalTableBody>
                                    {apps.length ? (
                                        apps.map((app, index) => (
                                            <MinimalTableRow key={app.name}>
                                                <MinimalTableCell component="th" scope="row" align="left">
                                                    <Typography>{app.name}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{app.url}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{app.comment}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="right">
                                                    <DangerOutlinedButton
                                                        name="hosted-apk-delete"
                                                        onClick={() => handleDelete(index)}
                                                        tooltipProps={{ title: t("common:button.delete") }}
                                                    >
                                                        <i className="fa-solid fa-trash" />
                                                    </DangerOutlinedButton>
                                                </MinimalTableCell>
                                            </MinimalTableRow>
                                        ))
                                    ) : (
                                        <MinimalTableRow>
                                            <MinimalTableCell align="center" colSpan={4}>
                                                <Typography>{t("pages:provisioningPage.table.noHostedApks")}</Typography>
                                            </MinimalTableCell>
                                        </MinimalTableRow>
                                    )}
                                </MinimalTableBody>
                            </MinimalTable>
                        </MinimalTableContainer>
                        <Row style={{ marginTop: theme.spacing(4), paddingBottom: theme.spacing(3) }}>
                            <TextField
                                name="name"
                                label={t("pages:provisioningPage.input.name.caption")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                            <TextField name="url" label={t("common:label.url")} disabled={!provisionings.length} fullWidth />
                        </Row>
                        <Row>
                            <TextField
                                name="comment"
                                label={t("common:label.comment")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                            <PrimaryOutlinedButton onClick={form.submit} disabled={!provisionings.length} name="hosted-apk-add">
                                {t("common:button.add")}
                            </PrimaryOutlinedButton>
                        </Row>
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>
            )}
        />
    );
};
